import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ListPlus, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { salesApi, type SalesListQuery } from '../../../api/endpoints';
import { queryKeys, salesListQuery } from '../../../api/queries';
import type { BulkTask, SalesDocumentSummary } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { Button } from '../../../ui/Button';
import { AsyncComboBox } from '../../../ui/ComboBox';
import { Callout, EmptyState } from '../../../ui/Feedback';
import { Money } from '../../../ui/Layout';
import { QueryError } from '../../../ui/QueryError';
import { DataTable, Pagination, type Column } from '../../../ui/Table';
import { UserText } from '../../../ui/Bidi';
import type { InvoiceListSearch } from '../InvoiceListPage';
import { TaxStatusBadge, taxAxisOf } from '../status';
import styles from './Batch.module.css';
import { BATCH_MAX, itemOf, type BatchItem } from './batchStore';
import { criteriaLabel, listQueryOf, type BatchCriteria } from './criteria';
import { candidateQuery } from './rules';

const PREVIEW_SIZES = [25, 50, 100];
const SEARCH_LIMIT = 10;

/** What put-all did, for the notice under the button. */
export type PutAllNotice = { kind: 'full' } | { kind: 'trimmed'; total: number } | null;

/**
 * Step 2, the documents (BLOCK-1B-BULK-DESIGN §3, BD-4 Option A). Documents enter ONLY through the
 * batch's own controls: the add field at the head of the step (one document, opens after typing),
 * and «أضف كل النتائج، حتى 100 مستند» for the carried criteria. The candidates are a read-only
 * preview: no control, badge or text about the batch on a row, and a row click never adds (AC-B3).
 */
export function DocumentsStep({
  orgId,
  task,
  criteria,
  items,
  locked,
  lockedByRun = false,
  onNew,
  notice,
  onAdd,
  onPutAll,
  onBackToTask,
}: {
  orgId: string;
  task: BulkTask | null;
  criteria: BatchCriteria;
  items: readonly BatchItem[];
  /** A run is in progress, or the batch holds a run's results: nothing may enter the batch. */
  locked: boolean;
  /** The batch holds a run's results (RIB-01): say why it is locked, and offer a new batch. */
  lockedByRun?: boolean;
  onNew?: () => void;
  notice: PutAllNotice;
  onAdd: (item: BatchItem) => void;
  onPutAll: (candidates: SalesDocumentSummary[], total: number) => void;
  onBackToTask: () => void;
}) {
  const { t } = useI18n();
  if (!task)
    return (
      <div className={styles.block} data-testid="batch-no-task">
        <p>{t.batch.noTask}</p>
        <div>
          <Button variant="secondary" onPress={onBackToTask}>
            {t.editor.review.backTo(t.batch.steps.task)}
          </Button>
        </div>
      </div>
    );
  return (
    <>
      {lockedByRun ? (
        <div className={styles.block} data-testid="batch-locked-by-run">
          <Callout tone="info" role="note">
            {t.batch.lockedByRun}
          </Callout>
          {onNew ? (
            <div>
              <Button variant="secondary" onPress={onNew} testId="batch-new-from-documents">
                {t.batch.results.new}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      <AddDocument orgId={orgId} task={task} items={items} locked={locked} onAdd={onAdd} />
      <FromResults
        orgId={orgId}
        task={task}
        criteria={criteria}
        items={items}
        locked={locked}
        notice={notice}
        onPutAll={onPutAll}
        onBackToTask={onBackToTask}
      />
    </>
  );
}

/** «مستند بعينه»: the add field at the head of step 2 (OP-5, DEC-024), never in the rail. */
function AddDocument({
  orgId,
  task,
  items,
  locked,
  onAdd,
}: {
  orgId: string;
  task: BulkTask;
  items: readonly BatchItem[];
  locked: boolean;
  onAdd: (item: BatchItem) => void;
}) {
  const { t } = useI18n();
  const full = items.length >= BATCH_MAX;
  const inBatch = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  return (
    <section className={styles.block} aria-labelledby="batch-one-title">
      <h3 id="batch-one-title" className={styles.blockTitle}>
        {t.batch.oneDocument}
      </h3>
      <div data-testid="batch-add-field" className={styles.addField}>
        <AsyncComboBox<SalesDocumentSummary>
          label={t.batch.addField}
          fieldName="batch-add"
          value={null}
          selectedItem={null}
          clearOnPick
          opensOn="input"
          placeholder={t.sales.searchPlaceholder}
          isDisabled={full || locked}
          {...(full ? { description: t.batch.full } : {})}
          onChange={(doc) => {
            if (doc) onAdd(itemOf(doc));
          }}
          queryFor={(q) => ({
            queryKey: [
              ...queryKeys.org(orgId),
              'sales',
              'batch-add',
              task,
              q,
              [...inBatch].join(','),
            ],
            // The whole organization, the task's documents only; those in the batch are left out.
            queryFn: async ({ signal }) =>
              (
                await salesApi.list(
                  orgId,
                  {
                    tab: 'invoices',
                    q,
                    eligibleFor: task,
                    limit: Math.min(SEARCH_LIMIT + inBatch.size, 200),
                  },
                  signal,
                )
              ).items
                .filter((d) => !inBatch.has(d.id))
                .slice(0, SEARCH_LIMIT),
            staleTime: 0,
          })}
          getKey={(d) => d.id}
          getLabel={(d) => d.number ?? d.id}
          renderItem={(d) => <CandidateOption doc={d} />}
        />
      </div>
    </section>
  );
}

/** An option of the add field: number · buyer · total · ETA status; it holds no button. */
function CandidateOption({ doc }: { doc: SalesDocumentSummary }) {
  const { t } = useI18n();
  const axis = taxAxisOf(doc);
  return (
    <span className={styles.option}>
      <span className={styles.mono} dir="ltr">
        {doc.number ?? '—'}
      </span>
      <span aria-hidden="true">·</span>
      <UserText>{doc.cash ? t.sales.cash : (doc.receiverName ?? t.sales.noCustomer)}</UserText>
      <span aria-hidden="true">·</span>
      <Money value={doc.totalAmount} currency="EGP" />
      {axis ? <TaxStatusBadge value={axis} /> : null}
    </span>
  );
}

/** «من نتائج القائمة»: the criteria line, put-all and the read-only preview. */
function FromResults({
  orgId,
  task,
  criteria,
  items,
  locked,
  notice,
  onPutAll,
  onBackToTask,
}: {
  orgId: string;
  task: BulkTask;
  criteria: BatchCriteria;
  items: readonly BatchItem[];
  locked: boolean;
  notice: PutAllNotice;
  onPutAll: (candidates: SalesDocumentSummary[], total: number) => void;
  onBackToTask: () => void;
}) {
  const { t, formatDateTime } = useI18n();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [putting, setPutting] = useState(false);
  const base: SalesListQuery = listQueryOf(criteria);
  const preview = useQuery(salesListQuery(orgId, candidateQuery(task, base, page, limit)));
  const rows = preview.data?.items ?? [];
  const full = items.length >= BATCH_MAX;

  const putAll = async () => {
    setPutting(true);
    try {
      const all = await salesApi.list(
        orgId,
        candidateQuery(task, base, 1, Math.min(200, BATCH_MAX + items.length)),
      );
      onPutAll(all.items, all.total);
    } finally {
      setPutting(false);
    }
  };

  const columns: Column<SalesDocumentSummary>[] = [
    {
      key: 'tax',
      header: t.sales.columns.taxStatus,
      cell: (r) => {
        const axis = taxAxisOf(r);
        return axis ? <TaxStatusBadge value={axis} /> : '—';
      },
    },
    {
      key: 'number',
      header: t.sales.columns.number,
      isPrimary: true,
      // The number link opens the document; the batch is kept (it lives in the tab).
      cell: (r) => (
        <Link
          to="/orgs/$orgId/sales/invoices/$docId"
          params={{ orgId, docId: r.id }}
          className={styles.numberLink}
          dir="ltr"
        >
          {r.number ?? '—'}
        </Link>
      ),
    },
    {
      key: 'buyer',
      header: t.sales.columns.customer,
      cell: (r) => (
        <UserText>{r.cash ? t.sales.cash : (r.receiverName ?? t.sales.noCustomer)}</UserText>
      ),
    },
    {
      key: 'amount',
      header: t.sales.columns.amount,
      align: 'end',
      cell: (r) => <Money value={r.totalAmount} currency="EGP" />,
    },
    {
      key: 'updated',
      header: t.sales.columns.updated,
      cell: (r) => formatDateTime(r.lastActivityAt),
    },
  ];

  return (
    <section className={styles.block} aria-labelledby="batch-results-block-title">
      <h3 id="batch-results-block-title" className={styles.blockTitle}>
        {t.batch.fromResults}
      </h3>
      <p className={styles.criteria}>
        <span data-testid="batch-criteria">{t.batch.criteria(criteriaLabel(t, criteria))}</span>{' '}
        <Link
          to="/orgs/$orgId/sales/invoices"
          params={{ orgId }}
          search={criteria as InvoiceListSearch}
          className={styles.textLink}
          data-testid="batch-change-criteria"
        >
          {t.batch.changeCriteria}
        </Link>
      </p>
      {preview.data && preview.data.total > 0 ? (
        <div>
          <Button
            variant="secondary"
            icon={<ListPlus size={16} aria-hidden="true" />}
            onPress={() => void putAll()}
            isPending={putting}
            isDisabled={full || locked}
            testId="batch-put-all"
          >
            {t.batch.putAll}
          </Button>
        </div>
      ) : null}
      {full || notice ? (
        // When the batch is full this notice is the visible reason the button is disabled.
        <div data-testid="batch-notice" {...(full ? { 'data-blocker': 'full' } : {})}>
          <Callout tone={notice?.kind === 'trimmed' ? 'info' : 'warning'} role="status">
            {notice?.kind === 'trimmed' ? t.batch.trimmed(String(notice.total)) : t.batch.full}
          </Callout>
        </div>
      ) : null}
      {preview.isError ? (
        <QueryError error={preview.error} onRetry={() => void preview.refetch()} />
      ) : (
        <>
          <DataTable<SalesDocumentSummary>
            caption={t.batch.preview.caption}
            testId="batch-preview"
            columns={columns}
            rows={rows}
            getRowKey={(r) => r.id}
            getRowTestId={(r) => `preview-${r.number ?? r.id}`}
            isLoading={preview.isLoading}
            empty={
              <EmptyState
                icon={<SearchX size={28} />}
                title={t.batch.candidatesEmpty}
                action={
                  <div className={styles.actions}>
                    <Link
                      to="/orgs/$orgId/sales/invoices"
                      params={{ orgId }}
                      search={criteria as InvoiceListSearch}
                      className={styles.textLink}
                    >
                      {t.batch.changeCriteria}
                    </Link>
                    <Button variant="secondary" onPress={onBackToTask}>
                      {t.editor.review.backTo(t.batch.steps.task)}
                    </Button>
                  </div>
                }
              >
                {t.batch.candidatesEmptyBody}
              </EmptyState>
            }
          />
          {preview.data && preview.data.total > 0 ? (
            <div data-testid="batch-pagination">
              <Pagination
                page={page}
                limit={limit}
                total={preview.data.total}
                pageSizes={PREVIEW_SIZES}
                onPageChange={setPage}
                onLimitChange={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
              />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
